import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft,
  Handshake,
  Building2,
  Shield,
  Activity,
  Sparkles,
  Users,
  Beaker,
  Layers,
  MapPin,
  Phone,
  MessageCircle,
  ChevronRight,
  ClipboardList,
  ScanSearch,
  FileSignature,
  Settings2,
  RefreshCw,
  Check,
} from 'lucide-react';

const WHATSAPP_LINK =
  'https://wa.me/2250778415277?text=Bonjour%20Pharmacie%20SADA%2C%20je%20souhaite%20vous%20proposer%20un%20partenariat.';
const PHONE_LINK = 'tel:+2250778415277';

const OPPORTUNITES = [
  { icon: Shield, title: 'Conventions de prise en charge',
    text: 'Conventions pharmaceutiques pour les employés ou les bénéficiaires d\'une organisation.' },
  { icon: Layers, title: 'Disponibilité de médicaments',
    text: 'Fourniture de médicaments et produits de santé autorisés selon les besoins du partenaire.' },
  { icon: Activity, title: 'Prévention & sensibilisation',
    text: 'Actions de prévention et de sensibilisation à la santé auprès de vos publics.' },
  { icon: Sparkles, title: 'Services en officine',
    text: 'Suivi de la tension artérielle, de la glycémie et autres services autorisés en officine.' },
  { icon: Users, title: 'Accompagnement digital · SADAHealth',
    text: 'Accompagnement digital des patients grâce à notre plateforme SADAHealth.' },
  { icon: Building2, title: 'Solutions professionnelles · SADAB2B',
    text: 'Solutions professionnelles et organisationnelles avec notre offre SADAB2B.' },
  { icon: Beaker, title: 'Laboratoires & marques',
    text: 'Collaborations avec les laboratoires, distributeurs et marques de santé.' },
  { icon: Handshake, title: 'Autres projets compatibles',
    text: 'Toute autre initiative compatible avec les missions et capacités d\'une officine.' },
];

const METHODE = [
  { icon: ScanSearch, title: 'Compréhension du besoin',
    text: 'Nous prenons le temps d\'écouter et de reformuler votre demande.' },
  { icon: ClipboardList, title: 'Analyse de la faisabilité',
    text: 'Étude opérationnelle, réglementaire et logistique de votre proposition.' },
  { icon: FileSignature, title: 'Proposition d\'un cadre',
    text: 'Rédaction d\'un cadre de collaboration adapté à vos objectifs.' },
  { icon: Settings2, title: 'Responsabilités & modalités',
    text: 'Définition claire des responsabilités et des modalités pratiques.' },
  { icon: RefreshCw, title: 'Mise en œuvre & suivi',
    text: 'Déploiement puis suivi régulier du partenariat dans la durée.' },
];

const ATOUTS = [
  'Implantation accessible à Cocody Angré 7e Tranche, carrefour Bluetooth.',
  'Équipe pharmaceutique encadrée par des professionnels qualifiés.',
  'Officine moderne, spacieuse et accueillante.',
  'Outils digitaux facilitant le suivi et l\'organisation des services.',
  'Approche personnalisée des besoins de chaque partenaire.',
  'Un interlocuteur clairement identifié pour le suivi de la collaboration.',
];

const CHECKLIST = [
  'Le nom de votre organisation.',
  'Le type de partenariat recherché.',
  'Les bénéficiaires concernés.',
  'La zone géographique.',
  'La durée envisagée.',
  'Les services ou produits attendus.',
  'Les coordonnées de la personne à contacter.',
];

const FAQ = [
  {
    q: 'Quelle pharmacie contacter pour un partenariat à Abidjan ?',
    a: 'La Pharmacie Saint Ange Des Amis, également appelée Pharmacie SADA, située à Cocody Angré, étudie les propositions de partenariat des entreprises, assurances, ONG, établissements de santé, laboratoires et fournisseurs.',
  },
  {
    q: 'Quels types d\'organisations peuvent proposer un partenariat à SADA ?',
    a: 'Les entreprises, assurances, mutuelles, établissements de santé, ONG, associations, écoles, laboratoires, distributeurs et marques de santé peuvent soumettre une proposition à la Pharmacie SADA.',
  },
  {
    q: 'Comment proposer un partenariat à la Pharmacie SADA ?',
    a: 'L\'organisation peut contacter la Pharmacie SADA par téléphone ou WhatsApp en présentant son besoin, les bénéficiaires concernés, la zone géographique et la durée envisagée.',
  },
  {
    q: 'La Pharmacie SADA peut-elle intervenir en dehors de Cocody ?',
    a: 'Les demandes concernant d\'autres zones d\'Abidjan ou de Côte d\'Ivoire sont étudiées au cas par cas, selon la nature du projet et les capacités opérationnelles nécessaires.',
  },
];

const PartenariatsPage = ({ onBack, onNavigate }) => {
  // SEO — titre, meta description, canonical URL et JSON-LD Schema.org Pharmacy.
  // Tout est nettoyé à la sortie pour ne pas polluer les autres pages.
  useEffect(() => {
    const prevTitle = document.title;
    const prevMeta = document.querySelector('meta[name="description"]');
    const prevMetaContent = prevMeta ? prevMeta.getAttribute('content') : null;

    // Title
    document.title = 'Partenariat pharmacie à Abidjan | Pharmacie SADA';

    // Meta description
    let m = document.querySelector('meta[name="description"]');
    if (!m) {
      m = document.createElement('meta');
      m.name = 'description';
      document.head.appendChild(m);
    }
    m.setAttribute(
      'content',
      'La Pharmacie SADA, située à Cocody Angré, étudie des partenariats avec les entreprises, assurances, ONG, établissements de santé et fournisseurs.',
    );

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute('href') : null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://phciesada.org/partenariats');

    // Robots meta — s'assure qu'il n'y a AUCUN noindex sur cette page
    let robotsMeta = document.querySelector('meta[name="robots"]');
    const prevRobots = robotsMeta ? robotsMeta.getAttribute('content') : null;
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.setAttribute('name', 'robots');
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute('content', 'index, follow');

    // JSON-LD Schema.org Pharmacy — entité autonome, aucune personne mentionnée.
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Pharmacy',
      name: 'Pharmacie Saint Ange Des Amis',
      alternateName: 'Pharmacie SADA',
      url: 'https://phciesada.org',
      telephone: '+2250778415277',
      contactPoint: [
        { '@type': 'ContactPoint', telephone: '+2250778415277', contactType: 'customer service' },
        { '@type': 'ContactPoint', telephone: '+2252722300572', contactType: 'customer service' },
      ],
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Angré 7e Tranche, carrefour Bluetooth',
        addressLocality: 'Cocody',
        addressRegion: 'Abidjan',
        addressCountry: 'CI',
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '07:30',
          closes: '20:00',
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: 'Saturday',
          opens: '07:30',
          closes: '13:00',
        },
      ],
      areaServed: [
        { '@type': 'Place', name: 'Cocody' },
        { '@type': 'City', name: 'Abidjan' },
      ],
    };
    const scriptEl = document.createElement('script');
    scriptEl.type = 'application/ld+json';
    scriptEl.id = 'ld-json-partenariats';
    scriptEl.text = JSON.stringify(jsonLd);
    document.head.appendChild(scriptEl);

    return () => {
      document.title = prevTitle;
      if (prevMeta && prevMetaContent !== null) prevMeta.setAttribute('content', prevMetaContent);
      if (canonical && prevCanonical !== null) canonical.setAttribute('href', prevCanonical);
      else if (canonical && prevCanonical === null) canonical.parentNode?.removeChild(canonical);
      if (robotsMeta && prevRobots !== null) robotsMeta.setAttribute('content', prevRobots);
      else if (robotsMeta && prevRobots === null) robotsMeta.parentNode?.removeChild(robotsMeta);
      const s = document.getElementById('ld-json-partenariats');
      if (s) s.parentNode?.removeChild(s);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAF6F1] via-white to-[#FAF6F1]" data-testid="page-partenariats">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#E8DDD4] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button
            onClick={onBack}
            variant="ghost"
            size="sm"
            className="text-[#5D4E37] hover:bg-[#F5EDE4]"
            data-testid="partenariats-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
          <h1 className="font-bold text-[#5D4E37] text-sm sm:text-base">Partenariats · Pharmacie SADA</h1>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#5D4E37]/5 via-transparent to-[#C9A086]/10 pointer-events-none" />
        <div className="max-w-6xl mx-auto px-4 pt-10 pb-8 sm:pt-14 sm:pb-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8 lg:gap-12 items-center">
            {/* Colonne texte */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center lg:text-left"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#5D4E37] text-white text-xs font-medium mb-4">
                <Handshake className="w-3.5 h-3.5" />
                Partenariats & Collaborations
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-[#5D4E37] leading-tight">
                Une pharmacie partenaire des entreprises et organisations à Abidjan
              </h2>
              <p className="mt-5 text-base sm:text-lg text-[#78716C] leading-relaxed">
                Vous représentez une entreprise, une assurance, une mutuelle, un établissement de
                santé, une ONG, une association, une école, un laboratoire, un distributeur ou une
                marque de santé ?
              </p>
              <p className="mt-3 text-base sm:text-lg text-[#78716C] leading-relaxed">
                La <strong className="text-[#5D4E37]">Pharmacie Saint Ange Des Amis</strong>, également appelée
                <strong className="text-[#5D4E37]"> Pharmacie SADA</strong>, est une officine privée située à
                Cocody Angré 7e Tranche, au carrefour Bluetooth à Abidjan.
              </p>
              <p className="mt-3 text-base sm:text-lg text-[#78716C] leading-relaxed">
                SADA étudie les propositions de partenariat avec les organisations recherchant une
                pharmacie accessible, moderne et capable de construire une collaboration adaptée à
                leurs besoins, dans le respect de la réglementation ivoirienne.
              </p>

              <div className="flex flex-wrap gap-3 justify-center lg:justify-start mt-8">
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="partenariats-cta-whatsapp">
                  <Button className="bg-[#25D366] hover:bg-[#1EBE5A] text-white rounded-full px-6 h-11">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Proposer un partenariat sur WhatsApp
                  </Button>
                </a>
                <a href={PHONE_LINK} data-testid="partenariats-cta-phone">
                  <Button variant="outline" className="border-[#5D4E37] text-[#5D4E37] hover:bg-[#5D4E37] hover:text-white rounded-full px-6 h-11">
                    <Phone className="w-4 h-4 mr-2" />
                    Appeler la Pharmacie SADA
                  </Button>
                </a>
              </div>
            </motion.div>

            {/* Colonne avatar SADA */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{
                opacity: 1,
                scale: 1,
                y: [0, -8, 0],
              }}
              transition={{
                opacity: { duration: 0.6, delay: 0.15 },
                scale: { duration: 0.6, delay: 0.15 },
                y: {
                  duration: 4.5,
                  delay: 0.8,
                  repeat: Infinity,
                  ease: 'easeInOut',
                },
              }}
              whileHover={{
                scale: 1.04,
                rotate: [0, -1.5, 1.5, 0],
                transition: { duration: 0.6, ease: 'easeInOut' },
              }}
              className="relative flex justify-center lg:justify-end order-first lg:order-none cursor-pointer"
              data-testid="partenariats-avatar"
            >
              {/* Halo décoratif derrière l'avatar */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <motion.div
                  animate={{ scale: [1, 1.08, 1], opacity: [0.7, 1, 0.7] }}
                  transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                  className="w-64 h-64 sm:w-72 sm:h-72 lg:w-80 lg:h-80 rounded-full bg-gradient-to-br from-[#C9A086]/25 via-[#5D4E37]/10 to-transparent blur-2xl"
                />
              </div>
              <img
                src="/images/sada-avatar.png"
                alt="Avatar de la Pharmacie SADA — pharmacienne partenaire à Abidjan"
                loading="eager"
                className="relative z-10 w-56 sm:w-64 lg:w-full max-w-[360px] h-auto drop-shadow-[0_20px_40px_rgba(93,78,55,0.25)]"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* OPPORTUNITÉS */}
      <section className="max-w-6xl mx-auto px-4 py-10 sm:py-14">
        <div className="text-center mb-8">
          <h3 className="text-2xl sm:text-3xl font-serif font-bold text-[#5D4E37]">
            Les partenariats que nous pouvons étudier
          </h3>
          <p className="mt-3 text-sm text-[#78716C] max-w-2xl mx-auto">
            Chaque proposition est étudiée au cas par cas en fonction des besoins, des capacités
            opérationnelles de SADA et de la réglementation applicable.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {OPPORTUNITES.map((o, i) => {
            const Icon = o.icon;
            return (
              <motion.div
                key={o.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: i * 0.04 }}
              >
                <Card className="h-full border-[#E8DDD4] hover:shadow-lg transition-shadow bg-white">
                  <CardContent className="p-5">
                    <div className="w-11 h-11 rounded-xl bg-[#5D4E37]/10 text-[#5D4E37] flex items-center justify-center mb-3">
                      <Icon className="w-5 h-5" />
                    </div>
                    <h4 className="font-semibold text-[#5D4E37] mb-1">{o.title}</h4>
                    <p className="text-sm text-[#78716C] leading-relaxed">{o.text}</p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* MÉTHODE */}
      <section className="bg-[#F5EDE4]/40 border-y border-[#E8DDD4]">
        <div className="max-w-5xl mx-auto px-4 py-10 sm:py-14">
          <div className="text-center mb-8">
            <h3 className="text-2xl sm:text-3xl font-serif font-bold text-[#5D4E37]">
              Notre méthode de collaboration
            </h3>
            <p className="mt-2 text-sm text-[#78716C]">
              Un processus clair et professionnel, du premier contact au suivi dans la durée.
            </p>
          </div>
          <ol className="space-y-3">
            {METHODE.map((m, i) => {
              const Icon = m.icon;
              return (
                <li key={m.title} className="flex gap-4 items-start bg-white rounded-xl border border-[#E8DDD4] p-4 sm:p-5">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#5D4E37] text-white flex items-center justify-center font-bold text-sm">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-[#5D4E37] flex items-center gap-2">
                      <Icon className="w-4 h-4 text-[#C9A086]" />
                      {m.title}
                    </h4>
                    <p className="text-sm text-[#78716C] mt-0.5">{m.text}</p>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* ATOUTS */}
      <section className="max-w-5xl mx-auto px-4 py-10 sm:py-14">
        <div className="text-center mb-8">
          <h3 className="text-2xl sm:text-3xl font-serif font-bold text-[#5D4E37]">
            Pourquoi choisir la Pharmacie SADA ?
          </h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-3xl mx-auto">
          {ATOUTS.map((a) => (
            <div key={a} className="flex items-start gap-3 bg-white rounded-xl border border-[#E8DDD4] px-4 py-3">
              <div className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-full bg-[#5D4E37]/10 flex items-center justify-center">
                <Check className="w-3.5 h-3.5 text-[#5D4E37]" />
              </div>
              <p className="text-sm text-[#5D4E37]">{a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PROPOSER UN PARTENARIAT — CHECKLIST */}
      <section className="bg-[#5D4E37] text-white">
        <div className="max-w-4xl mx-auto px-4 py-10 sm:py-14">
          <div className="text-center mb-8">
            <h3 className="text-2xl sm:text-3xl font-serif font-bold">Proposer un partenariat à SADA</h3>
            <p className="mt-2 text-sm text-white/80">
              Pour nous permettre d&apos;étudier votre proposition, indiquez :
            </p>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
            {CHECKLIST.map((c) => (
              <li key={c} className="flex items-start gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/15">
                <ChevronRight className="w-4 h-4 mt-0.5 text-[#F5EDE4] flex-shrink-0" />
                <span className="text-sm">{c}</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-3 justify-center mt-8">
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#25D366] hover:bg-[#1EBE5A] text-white rounded-full px-6 h-11">
                <MessageCircle className="w-4 h-4 mr-2" />
                Proposer un partenariat sur WhatsApp
              </Button>
            </a>
            <a href={PHONE_LINK}>
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#5D4E37] rounded-full px-6 h-11 bg-transparent">
                <Phone className="w-4 h-4 mr-2" />
                Appeler la Pharmacie SADA
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* COORDONNÉES */}
      <section className="max-w-4xl mx-auto px-4 py-10 sm:py-14">
        <Card className="border-[#E8DDD4]">
          <CardContent className="p-6 sm:p-8">
            <h3 className="text-xl sm:text-2xl font-serif font-bold text-[#5D4E37] mb-4">
              Coordonnées de la Pharmacie SADA
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 text-sm">
              <div>
                <div className="font-semibold text-[#5D4E37]">Pharmacie Saint Ange Des Amis</div>
                <div className="text-[#78716C]">Pharmacie SADA</div>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 text-[#C9A086] flex-shrink-0" />
                <div className="text-[#5D4E37]">
                  Angré 7<sup>e</sup> Tranche, carrefour Bluetooth<br />
                  Cocody, Abidjan, Côte d&apos;Ivoire
                </div>
              </div>
              <div className="flex items-start gap-2">
                <MessageCircle className="w-4 h-4 mt-0.5 text-[#25D366] flex-shrink-0" />
                <div className="text-[#5D4E37]">
                  <div className="font-medium">Téléphone & WhatsApp</div>
                  <a href="tel:+2250778415277" className="text-[#5D4E37] hover:underline">+225 07 78 41 52 77</a>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Phone className="w-4 h-4 mt-0.5 text-[#C9A086] flex-shrink-0" />
                <div className="text-[#5D4E37]">
                  <div className="font-medium">Téléphone fixe</div>
                  <a href="tel:+2252722300572" className="text-[#5D4E37] hover:underline">+225 27 22 30 05 72</a>
                </div>
              </div>
              <div className="sm:col-span-2 pt-2 border-t border-[#E8DDD4]">
                <a href="https://phciesada.org" className="text-sm text-[#5D4E37] hover:underline">
                  https://phciesada.org
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-4 pb-14">
        <div className="text-center mb-8">
          <h3 className="text-2xl sm:text-3xl font-serif font-bold text-[#5D4E37]">Questions fréquentes</h3>
        </div>
        <div className="space-y-3">
          {FAQ.map((f, i) => (
            <Card key={i} className="border-[#E8DDD4]">
              <CardContent className="p-5">
                <h4 className="font-semibold text-[#5D4E37] mb-2">{f.q}</h4>
                <p className="text-sm text-[#78716C] leading-relaxed">{f.a}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* FOOTER ACTIONS */}
      <section className="border-t border-[#E8DDD4] bg-[#FAF6F1]">
        <div className="max-w-4xl mx-auto px-4 py-8 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-[#78716C]">Envie de démarrer une conversation ? Un simple message suffit.</p>
          <div className="flex flex-wrap gap-2">
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#25D366] hover:bg-[#1EBE5A] text-white rounded-full h-10">
                <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
              </Button>
            </a>
            <a href={PHONE_LINK}>
              <Button variant="outline" className="border-[#5D4E37] text-[#5D4E37] hover:bg-[#5D4E37] hover:text-white rounded-full h-10">
                <Phone className="w-4 h-4 mr-2" /> Appeler
              </Button>
            </a>
            {onNavigate && (
              <Button variant="ghost" className="text-[#5D4E37] rounded-full h-10" onClick={() => onNavigate('contact')}>
                Nous contacter
              </Button>
            )}
            {onNavigate && (
              <Button
                variant="ghost"
                className="text-[#5D4E37] rounded-full h-10"
                onClick={() => onNavigate('recrutement')}
                data-testid="partenariats-link-recrutement"
              >
                Rejoindre l'équipe
              </Button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartenariatsPage;
